/**
 * hostBridge — coexistence routing helpers for the AngularJS-host ↔ standalone-host
 * cutover (Phase E).
 *
 * During the rollout window two hosts are live at the same time:
 *
 *   • Legacy AngularJS shell  → e.g. `www.sgpdev.com`
 *   • Standalone React bundle → e.g. `app.sgpdev.com`
 *
 * Bookmarks, deep links from emails, and links from the AngularJS sidebar may
 * still point at the legacy host long after a tenant has been moved to the
 * standalone host. Browser-side this module:
 *
 *   • `redirectFromLegacyHost()` — call once on app boot. If the standalone
 *     bundle finds itself loaded at the legacy host (because Infra hasn't
 *     completed the proxy/DNS cutover yet for this tenant, or someone hit the
 *     standalone JS on the wrong domain via a stale cached HTML page),
 *     hard-redirect to the standalone host preserving the hash + query string.
 *   • `redirectFromStandaloneHost()` — opposite direction. If the AngularJS
 *     shell is hit at the standalone host (rare but possible during partial
 *     deploys), redirect back so AngularJS isn't asked to render at a hostname
 *     it doesn't expect.
 *
 * The two host names default to `STANDALONE_HOST_DEFAULT` / `LEGACY_HOST_DEFAULT`
 * but can be overridden via `VITE_STANDALONE_HOST` / `VITE_LEGACY_HOST` env
 * variables at build time so non-prod environments can set their own pair
 * (e.g. `app-beta.sgpdev.com` / `beta.sgpdev.com`).
 *
 * Anything more sophisticated (per-tenant rollout, A/B sampling) goes in
 * `tenantRollout.ts`.
 */

const STANDALONE_HOST_DEFAULT = 'app.sgpdev.com';
const LEGACY_HOST_DEFAULT = 'www.sgpdev.com';

function readEnv(key: 'VITE_STANDALONE_HOST' | 'VITE_LEGACY_HOST'): string | undefined {
  // import.meta.env is replaced at build time by Vite; missing values come back as ''.
  const raw = (import.meta.env as Record<string, string | undefined>)[key];
  return raw && raw.trim() ? raw.trim() : undefined;
}

export function getStandaloneHost(): string {
  return readEnv('VITE_STANDALONE_HOST') ?? STANDALONE_HOST_DEFAULT;
}

export function getLegacyHost(): string {
  return readEnv('VITE_LEGACY_HOST') ?? LEGACY_HOST_DEFAULT;
}

interface HostRedirectOptions {
  /**
   * If true, navigation uses `replace()` so the user's back-button history
   * doesn't include the wrong-host URL. Default true.
   */
  replace?: boolean;
}

/** Build the equivalent URL on a different host, preserving path / query / hash. */
function rebuildOnHost(targetHost: string): string {
  const { protocol, port, pathname, search, hash } = window.location;
  // Drop the port unless it's a non-standard one we genuinely need to keep.
  // Production deploys typically run on the default 443; dev keeps the port.
  const portPart = port && port !== '443' && port !== '80' ? `:${port}` : '';
  return `${protocol}//${targetHost}${portPart}${pathname}${search}${hash}`;
}

function shouldSkipRedirect(currentHost: string): boolean {
  // Never redirect the local dev server, the hosts-file dev alias, or unit-test
  // environments. These are intentional non-prod hosts.
  if (currentHost === 'localhost' || currentHost === '127.0.0.1') return true;
  if (currentHost === 'dev.sgpdev.com') return true;
  return false;
}

/**
 * If the standalone bundle is currently being served from the legacy host,
 * hard-redirect to the standalone host. No-op when already on the standalone
 * host or in a dev environment.
 *
 * Returns `true` if a redirect was triggered (caller may want to short-circuit
 * the rest of the boot sequence since the page is about to navigate).
 */
export function redirectFromLegacyHost(opts: HostRedirectOptions = {}): boolean {
  const currentHost = window.location.hostname;
  if (shouldSkipRedirect(currentHost)) return false;

  const legacy = getLegacyHost();
  const standalone = getStandaloneHost();
  if (currentHost !== legacy) return false;
  if (currentHost === standalone) return false;

  const target = rebuildOnHost(standalone);
  if (opts.replace !== false) {
    window.location.replace(target);
  } else {
    window.location.assign(target);
  }
  return true;
}

/**
 * Inverse of `redirectFromLegacyHost`. Use only inside the AngularJS shell
 * (not in the React bundle) — this exists in `hostBridge.ts` so the helper
 * can be referenced from a small AngularJS-side bootstrap script that lives
 * outside `src/` once Phase E rollout begins.
 *
 * Returns `true` if a redirect was triggered.
 */
export function redirectFromStandaloneHost(opts: HostRedirectOptions = {}): boolean {
  const currentHost = window.location.hostname;
  if (shouldSkipRedirect(currentHost)) return false;

  const legacy = getLegacyHost();
  const standalone = getStandaloneHost();
  if (currentHost !== standalone) return false;
  if (currentHost === legacy) return false;

  const target = rebuildOnHost(legacy);
  if (opts.replace !== false) {
    window.location.replace(target);
  } else {
    window.location.assign(target);
  }
  return true;
}
